import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';

const AvatarContext = createContext();

export const useAvatar = () => {
  const context = useContext(AvatarContext);
  if (!context) {
    throw new Error('useAvatar must be used within an AvatarProvider');
  }
  return context;
};

export const AvatarProvider = ({ children }) => {
  const { user } = useAuth();
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAvatar = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('avatar_type, avatar_color, avatar_initials, avatar_url')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        throw error;
      }

      if (data) {
        setAvatar({
          type: data.avatar_type,
          color: data.avatar_color,
          initials: data.avatar_initials,
          url: data.avatar_url
        });
      } else {
        // Create default avatar if no profile exists
        const defaultInitials = user.email.substring(0, 3).toUpperCase();
        const defaultAvatar = {
          type: 'initials',
          color: '#6366f1',
          initials: defaultInitials,
          url: null
        };
        setAvatar(defaultAvatar);
        
        // Also create the profile in the database
        try {
          await supabase
            .from('user_profiles')
            .insert({
              user_id: user.id,
              avatar_type: 'initials',
              avatar_color: '#6366f1',
              avatar_initials: defaultInitials,
              avatar_url: null
            });
        } catch (insertError) {
          console.error('Error creating default profile:', insertError);
        }
      }
    } catch (error) {
      console.error('Error fetching avatar:', error);
      setError(error.message);
      
      // Fallback to default avatar even on error
      const defaultInitials = user?.email?.substring(0, 3).toUpperCase() || 'U';
      setAvatar({
        type: 'initials',
        color: '#6366f1',
        initials: defaultInitials,
        url: null
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  const updateAvatar = useCallback(async (newAvatar) => {
    if (!user) return;

    try {
      // Update in database
      const { error } = await supabase
        .from('user_profiles')
        .update({
          avatar_type: newAvatar.type,
          avatar_color: newAvatar.color,
          avatar_initials: newAvatar.initials,
          avatar_url: newAvatar.url,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      // Update local state
      setAvatar(newAvatar);
      
      return { success: true };
    } catch (error) {
      console.error('Error updating avatar:', error);
      return { success: false, error: error.message };
    }
  }, [user]);

  useEffect(() => {
    fetchAvatar();
  }, [fetchAvatar]);

  const value = {
    avatar,
    loading,
    error,
    updateAvatar,
    refetch: fetchAvatar
  };

  return (
    <AvatarContext.Provider value={value}>
      {children}
    </AvatarContext.Provider>
  );
};
